import React, { useState, useEffect, useRef, startTransition, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { XMarkIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../contexts/PermissionContext';

/**
 * TabsManager API 使用说明
 * 
 * 新增的 API 方法已暴露到全局 window.tabsManager 对象，其他页面可以直接调用：
 * 
 * // 关闭指定标签页
 * const success = window.tabsManager.closeTab('tab_key');
 * 
 * // 关闭当前标签页
 * const success = window.tabsManager.closeCurrentTab();
 * 
 * // 通过路径关闭标签页
 * const success = window.tabsManager.closeTabByPath('/dashboard/products/create');
 * 
 * // 获取标签页信息
 * const tab = window.tabsManager.getTab('tab_key');
 * const hasTab = window.tabsManager.hasTab('tab_key');
 * const allTabs = window.tabsManager.getAllTabs();
 * const activeTab = window.tabsManager.getActiveTab();
 * 
 * // 状态管理
 * window.tabsManager.saveTabState('tab_key', state);
 * window.tabsManager.restoreTabState('tab_key');
 * window.tabsManager.clearTabState('tab_key');
 * 
 * 注意事项：
 * 1. 这些 API 完全复用现有的标签页管理逻辑，不会改变任何现有行为
 * 2. 所有方法都返回 boolean 值，表示操作是否成功
 * 3. API 只在开发和生产环境中可用，测试环境可能不可用
 * 4. 建议在使用前检查 API 是否可用：if (window.tabsManager)
 */

// 存储工具函数
const storage = {
  setItem: (key: string, value: any) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
      console.log('💾 [SessionStorage] 存储成功:', key, value);
    } catch (error) {
      console.error('❌ [SessionStorage] 存储失败:', error);
    }
  },
  
  getItem: (key: string) => {
    try {
      const item = sessionStorage.getItem(key);
      const result = item ? JSON.parse(item) : null;
      console.log('📖 [SessionStorage] 读取:', key, result ? '成功' : '不存在');
      return result;
    } catch (error) {
      console.error('❌ [SessionStorage] 读取失败:', error);
      return null;
    }
  },
  
  removeItem: (key: string) => {
    try {
      sessionStorage.removeItem(key);
      console.log('🗑️ [SessionStorage] 删除成功:', key);
    } catch (error) {
      console.error('❌ [SessionStorage] 删除失败:', error);
    }
  }
};

/**
 * 从 token 中提取稳定的用户标识（用于判断是否“换人”）。
 * 若为 JWT 则解析 payload 取 sub/user_id，避免 token 刷新导致误判为换用户并清空标签页。
 * 解析失败则退回整段 token（与原先行为一致）。
 */
function getStableUserIdFromToken(token: string | null): string {
  if (!token || typeof token !== 'string') return '';
  const trimmed = token.trim();
  if (!trimmed) return '';
  try {
    const parts = trimmed.split('.');
    if (parts.length !== 3) return trimmed;
    const payload = parts[1];
    if (!payload) return trimmed;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    const obj = JSON.parse(json) as { sub?: string; user_id?: string; id?: number };
    const id = obj?.sub ?? obj?.user_id ?? (obj?.id != null ? String(obj.id) : '');
    return typeof id === 'string' ? id : String(id ?? trimmed);
  } catch {
    return trimmed;
  }
}

export interface TabItem {
  key: string;
  title: string;
  path: string;
  icon?: React.ReactNode;
  closable?: boolean;
}

// 完整的标签页状态管理接口
export interface TabState {
  // 页面特定状态（内存缓存）
  pageState: {
    scrollPosition: number;
    expandedRows: string[];
    selectedItems: string[];
    customState?: any;
  };
  
  // 持久化状态（sessionStorage）
  persistentState: {
    userPreferences: any;
    lastViewSettings: any;
  };
  
  // 元数据
  metadata: {
    lastAccessTime: number;
    accessCount: number;
    isDirty: boolean;
    createdAt: number;
  };
}

// 标签页配置接口
export interface TabConfig {
  // 基础配置
  path: string;
  title: string;
  
  // 状态管理配置
  stateManagement: {
    // 是否保持状态到sessionStorage
    persistToStorage: boolean;
    
    // 状态保持策略
    keepStateStrategy: 'memory' | 'sessionStorage' | 'hybrid';
    
    // 需要永久保存的状态字段
    persistentFields: string[];
    
    // 状态过期时间（毫秒）
    stateExpiryTime?: number;
    
    // 是否在标签页关闭时清理状态
    clearOnClose: boolean;
  };
  
  // 生命周期回调
  lifecycle: {
    onTabActivate?: () => void;
    onTabDeactivate?: () => void;
    onTabClose?: () => void;
    onStateRestore?: (state: any) => void;
    onStateSave?: () => any;
  };
}

// 标签页状态管理器接口
export interface TabStateManager {
  // 保存状态
  saveState: (state: Partial<TabState>) => void;
  
  // 恢复状态
  restoreState: () => TabState | null;
  
  // 清理状态
  clearState: () => void;
  
  // 获取状态
  getState: () => TabState | null;
  
  // 检查状态是否存在
  hasState: () => boolean;
  
  // 更新状态
  updateState: (updates: Partial<TabState>) => void;
}

// 标签页生命周期配置接口（保持向后兼容）
export interface TabLifecycleConfig {
  keepState: boolean;              // 是否保留状态到内存
  persistToStorage: boolean;       // 是否保存到sessionStorage
  customStorageKey?: string;       // 自定义存储键名
  
  // 生命周期回调
  onTabActivate?: () => void;      // 标签页激活时
  onTabDeactivate?: () => void;    // 标签页失活时
  onTabClose?: () => void;         // 标签页关闭时
  
  // 状态管理回调
  onSaveState?: () => any;         // 保存状态时
  onRestoreState?: (state: any) => void;  // 恢复状态时
  onClearState?: () => void;       // 清理状态时
}

// 增强的标签页状态接口
export interface EnhancedTabItem extends TabItem {
  // 当前状态
  currentState?: TabState;
  
  // 配置信息
  config?: TabConfig;
  
  // 生命周期配置（保持向后兼容）
  lifecycleConfig?: TabLifecycleConfig;
  
  // 状态管理
  stateManager?: TabStateManager;
}

interface TabsManagerProps {
  onTabChange?: (activeKey: string) => void;
}

// 标签页标题映射（value为国际化key）
const tabTitleMap: Record<string, string> = {
  // 首页
  'dashboard': 'tab.dashboard',
  '/dashboard/admin': 'tab.dashboard',
  '/dashboard/staff': 'tab.dashboard',
  '/dashboard/customer': 'tab.dashboard',
  // 项目管理
  'projects': 'tab.projects',
  'projects-list': 'tab.projectsList',
  // 兜底：按路径最后一段命中（规范第二节），防止路径前缀变化时又退回显示原始路由片段
  'return-inspection-rules': 'tab.returnInspectionRules',
  'projects-create': 'tab.projectsCreate',
  'projects-groups': 'tab.projectsGroups',
  // 产品库
  'products': 'tab.products',
  'products-list': 'tab.productsList',
  'products-create': 'tab.productsCreate',
  'products-batch-create': 'tab.productsBatchCreate',
  // 收费系统
  '/dashboard/admin/billing-system': 'tab.billingSystem',
  'billing-system': 'tab.billingSystem',
  // 日志系统（管理员）
  '/dashboard/admin/log-system': 'tab.logSystem',
  '/dashboard/admin/log-system/config': 'tab.logSystemConfig',
  'log-system': 'tab.logSystem',
  'log-system-config': 'tab.logSystemConfig',
  // 费率试算
  '/dashboard/admin/billing/rate-preview': 'tab.ratePreview',
  // 工单系统（管理员/员工）
  '/dashboard/admin/work-orders': 'tab.workOrdersSystem',
  '/dashboard/admin/work-orders/templates': 'tab.workOrdersSystem',
  '/dashboard/admin/work-orders/routes': 'tab.workOrdersSystem',
  '/dashboard/admin/work-orders/templates/create': 'tab.workOrderTemplateCreate',
  '/dashboard/admin/work-orders/list': 'tab.customerWorkOrders',
  '/dashboard/staff/work-orders/list': 'tab.customerWorkOrders',
  // 执行待办（管理员/员工）
  '/dashboard/staff/execution': 'tab.executionTasks',
  '/dashboard/admin/execution': 'tab.executionTasks',
  'execution': 'tab.executionTasks',
  '/dashboard/staff/execution/instruction/:id': 'tab.instructionExecute',
  '/dashboard/admin/execution/instruction/:id': 'tab.instructionExecute',
  '/dashboard/staff/execution/batch/:id': 'tab.batchExecute',
  '/dashboard/admin/execution/batch/:id': 'tab.batchExecute',
  '/dashboard/staff/quick-tools/sort-and-count': 'tab.sortAndCount',
  '/dashboard/admin/quick-tools/sort-and-count': 'tab.sortAndCount',
  'sort-and-count': 'tab.sortAndCount',
  'work-orders-templates': 'tab.workOrderTemplates',
  'work-orders-list': 'tab.customerWorkOrders',
  // 仓内工单（internal）
  '/dashboard/admin/internal-work-orders/create': 'tab.internalWorkOrderCreate',
  '/dashboard/admin/internal-work-orders/list': 'tab.internalWorkOrders',
  '/dashboard/staff/internal-work-orders/list': 'tab.internalWorkOrders',
  'internal-work-orders-create': 'tab.internalWorkOrderCreate',
  'internal-work-orders-list': 'tab.internalWorkOrders',
  // 指令系统（管理员）
  '/dashboard/admin/instruction-system': 'tab.instructionSystem',
  'instruction-system': 'tab.instructionSystem',
  // 财务 - 收费记录
  '/dashboard/admin/finance/billing-records': 'tab.billingRecords',
  '/dashboard/customer/finance/billing-records': 'tab.billingRecords',
  'finance-billing-records': 'tab.billingRecords',
  'billing-records': 'tab.billingRecords',
  '/dashboard/admin/finance/bills': 'tab.financeBills',
  '/dashboard/customer/finance/bills': 'tab.financeBills',
  'finance-bills': 'tab.financeBills',
  'bills': 'tab.financeBills',
  '/dashboard/admin/finance/funds': 'tab.fundsAccount',
  '/dashboard/customer/finance/funds': 'tab.fundsAccount',
  'finance-funds': 'tab.fundsAccount',
  'funds': 'tab.fundsAccount',
  // 客户财务 - 收费规则
  '/dashboard/customer/finance/billing-rules': 'tab.billingRules',
  'finance-billing-rules': 'tab.billingRules',
  'billing-rules': 'tab.billingRules',
  // 入库管理
  'inbound': 'tab.inbound',
  'inbound-forecast': 'tab.inboundForecast',
  'inbound-forecast-list': 'tab.inboundForecastList',
  'inbound-forecast-create': 'tab.inboundForecastCreate',
  'inbound-forecast-bulk-create': 'tab.inboundForecastBulkCreate',
  'inbound-forecast-label': 'tab.inboundForecastLabel',
  'inbound-scan': 'tab.inboundScan',
  'inbound-records': 'tab.inboundRecords',
  'inbound-records-detail': 'tab.inboundRecordDetail',
  // 库内管理
  'inventory': 'tab.inventory',
  'inventory-current': 'tab.currentInventory',
  'inventory-history': 'tab.inventoryHistory',
  'inventory-config': 'tab.inventoryConfig',
  'inventory-labels': 'tab.inventoryLabels',
  // 出库管理
  'outbound': 'tab.outbound',
  'outbound-orders': 'tab.outboundOrders',
  'outbound-available': 'tab.outboundAvailable',
  'outbound-records': 'tab.outboundRecords',
  // 物流管理
  'logistics': 'tab.logistics',
  'logistics-providers': 'tab.logisticsProviders',
  'logistics-shipping-package': 'tab.shippingPackaging',
  'logistics-package-estimator': 'tab.packageEstimator',
  'logistics-shipping-rates': 'tab.shippingRates',
  'logistics-shipments': 'tab.shipments',
  'shipping-package': 'tab.shippingPackaging',
  // 指令管理
  'orders': 'tab.instructionsManagement',
  'orders-list': 'tab.instructionList',
  'orders-create': 'tab.instructionCreate',
  'instructions': 'tab.instructionsManagement',
  'instructions-list': 'tab.instructionList',
  'instructions-create': 'tab.instructionCreate',
  'bulk-create': 'tab.instructionBulkCreate',
  'instructions-bulk-create': 'tab.instructionBulkCreate',
  'instructions-import': 'tab.instructionImportCreate',
  'instructions-batch': 'tab.instructionBatch',
  '/dashboard/admin/instructions/batch': 'tab.instructionBatch',
  // 指令终审（管理员）：/dashboard/admin/instructions/:id/final-review
  // 说明：动态路由不会命中“完整路径映射”，因此保留 lastSegment 兜底键 final-review
  '/dashboard/admin/instructions/:id/final-review': 'tab.instructionFinalReview',
  '/dashboard/admin/admin/instructions/:id/final-review': 'tab.instructionFinalReview',
  'final-review': 'tab.instructionFinalReview',
  // 客户服务
  'customer-service': 'menu.customerService',
  'service-center': 'tab.customerServiceCenter',
  'customer-service-tickets': 'tab.customerServiceTickets',
  'tickets': 'tab.customerServiceTickets',
  'customer-service-guide': 'tab.customerServiceGuide',
  'guide': 'tab.customerServiceGuide',
  'customer-service-FAQ': 'tab.customerServiceFaq',
  'FAQ': 'tab.customerServiceFaq',
  'customer-service-tools': 'tab.customerServiceTools',
  'tools': 'tab.customerServiceTools',
  'customer-service-requests': 'tab.customerServiceRequests',
  'customer-service-faq': 'tab.customerServiceFaq',
  'customer-service-contact': 'tab.customerServiceContact',
  // 系统用户管理
  'users': 'tab.users',
  'users-list': 'tab.usersList',
  'users-create': 'tab.usersCreate',
  '/dashboard/admin/users/create': 'tab.usersCreate',
  'users-roles': 'tab.usersRoles',
  // 仓库管理
  'warehouses': 'warehouses',
  'warehouses-list': 'warehousesList',
  'warehouses-detail': 'warehousesDetail',
  // 新路径映射（单数 warehouse）
  '/dashboard/customer/warehouse/available': 'tab.warehousesAvailable',
  '/dashboard/admin/warehouse/available': 'tab.warehousesAvailable',
  '/dashboard/staff/warehouse/available': 'tab.warehousesAvailable',
  // 管理员：创建仓库页签标题
  '/dashboard/admin/warehouse/create': 'menu.createWarehouse',
  // 入库相关
  '/dashboard/customer/inbound/forecast/list': 'tab.inboundForecastList',
  '/dashboard/customer/inbound/records': 'tab.inboundRecords',
  '/dashboard/admin/inbound/forecast/list': 'tab.inboundForecastList',
  '/dashboard/admin/inbound/scan': 'tab.inboundScan',
  '/dashboard/admin/inbound/records': 'tab.inboundRecords',
  '/dashboard/admin/inbound/put-away': 'putAway.title',
  '/dashboard/staff/inbound/forecast/list': 'tab.inboundForecastList',
  '/dashboard/staff/inbound/scan': 'tab.inboundScan',
  '/dashboard/staff/inbound/records': 'tab.inboundRecords',
  '/dashboard/staff/inbound/put-away': 'putAway.title',
  // 库存管理（新前缀）
  '/dashboard/customer/inventory/current': 'tab.currentInventory',
  '/dashboard/customer/inventory/history': 'tab.inventoryHistory',
  '/dashboard/admin/inventory/current': 'tab.currentInventory',
  '/dashboard/admin/inventory/history': 'tab.inventoryHistory',
  '/dashboard/admin/inventory/location': 'tab.locationTable',
  '/dashboard/staff/inventory/current': 'tab.currentInventory',
  '/dashboard/staff/inventory/history': 'tab.inventoryHistory',
  '/dashboard/staff/inventory/location': 'tab.locationTable',
  'inventory-location': 'tab.locationTable',
  // 物流管理（新前缀）
  '/dashboard/customer/logistics/shipping-carriers': 'tab.logisticsProvidersList',
  '/dashboard/admin/logistics/shipping-carriers': 'tab.logisticsProvidersList',
  '/dashboard/staff/logistics/shipping-carriers': 'tab.logisticsProvidersList',
  '/dashboard/admin/logistics/shipping-package': 'tab.shippingPackaging',
  '/dashboard/customer/logistics/address-book': 'tab.customerAddressBook',
  '/dashboard/admin/logistics/address-book': 'tab.customerAddressBook',
  '/dashboard/logistics-system': 'tab.logisticsSystem',
  '/dashboard/admin/logistics-system': 'tab.logisticsSystem',
  'logistics-system': 'tab.logisticsSystem',
  // 产品（采用新路径）
  '/dashboard/customer/products/create': 'tab.productsCreate',
  '/dashboard/customer/products/list': 'tab.productsList',
  '/dashboard/customer/products/batch-create': 'tab.productsBatchCreate',
  '/dashboard/admin/products/create': 'tab.productsCreate',
  '/dashboard/admin/products/list': 'tab.productsList',
  '/dashboard/admin/products/batch-create': 'tab.productsBatchCreate',
  '/dashboard/staff/products/list': 'tab.productsList',
  '/dashboard/staff/products/batch-create': 'tab.productsBatchCreate',
  // 客户管理
  'customers': 'tab.customers',
  'customers-list': 'tab.customersList',
  'customers-create': 'tab.customersCreate',
  // 管理员完整路径（确保优先命中完整路径映射）
  '/dashboard/admin/customers/list': 'tab.customersList',
  '/dashboard/admin/customers/create': 'tab.customersCreate',
  // 项目组（统一显示为“项目组”）
  '/dashboard/customer/project-groups/list': 'menu.projectGroups',
  '/dashboard/customer/project-groups/create': 'tab.projectGroupsCreate',
  '/dashboard/customer/project-groups/:id': 'menu.projectGroups',
  '/dashboard/admin/project-groups/list': 'menu.projectGroups',
  '/dashboard/admin/project-groups/create': 'menu.projectGroups',
  '/dashboard/admin/project-groups/:id': 'menu.projectGroups',
  '/dashboard/staff/project-groups/list': 'menu.projectGroups',
  '/dashboard/staff/project-groups/:id': 'menu.projectGroups',
  // 项目（统一为新的路径）
  '/dashboard/customer/projects/create': 'tab.projectsCreate',
  '/dashboard/customer/projects/list': 'tab.projectsList',
  '/dashboard/customer/projects/basic-config': 'tab.projectBasicConfig',
  '/dashboard/customer/projects/return-inspection-rules': 'tab.returnInspectionRules',
  '/dashboard/customer/projects/inventory-config/create': 'tab.projectBasicConfigCreate',
  '/dashboard/admin/projects/create': 'tab.projectsCreate',
  '/dashboard/admin/projects/list': 'tab.projectsList',
  '/dashboard/admin/projects/basic-config': 'tab.projectBasicConfig',
  '/dashboard/admin/projects/return-inspection-rules': 'tab.returnInspectionRules',
  '/dashboard/admin/projects/inventory-config/create': 'tab.projectBasicConfigCreate',
  '/dashboard/staff/projects/list': 'tab.projectsList',
  '/dashboard/staff/projects/basic-config': 'tab.projectBasicConfig',
  '/dashboard/staff/projects/inventory-config/create': 'tab.projectBasicConfigCreate',
  // 偏好设定
  'account-preferences': 'tab.preferences',
  '/dashboard/account/preferences': 'tab.preferences',
  '/dashboard/admin/account/preferences': 'tab.preferences',
  '/dashboard/staff/account/preferences': 'tab.preferences',
  '/dashboard/customer/account/preferences': 'tab.preferences',
  // 个人资料
  'profile': 'tab.profile',
  'account-profile': 'tab.profile',
  '/dashboard/admin/account/profile': 'tab.profile',
  '/dashboard/staff/account/profile': 'tab.profile',
  '/dashboard/customer/account/profile': 'tab.profile',
  // 客户主账号 - 子账号管理
  '/dashboard/customer/account/sub-account': 'tab.subAccountManagement',
  // 系统配置 - 基础设置
  'system-settings': 'tab.basicSettings',
  '/dashboard/admin/system-settings': 'tab.basicSettings',
  // 权限管理
  'permission-management': 'menu.permissionManagement',
  '/dashboard/admin/permission-management': 'menu.permissionManagement',
  // 菜单管理
  'menu-management': 'menu.menuManagement',
  '/dashboard/admin/menu-management': 'menu.menuManagement',
  'content-list': 'tab.contentManagement',
  'content-edit': 'tab.contentEdit',
  'content-preview': 'tab.contentPreview',
  '/dashboard/admin/content/list': 'tab.contentManagement',
  '/dashboard/admin/content/edit/:id': 'tab.contentEdit',
  '/dashboard/admin/content/preview/:id': 'tab.contentPreview',
};

/**
 * 标签页管理器
 * 
 * 职责：
 * 1. 管理标签页列表（创建、切换、关闭）
 * 2. 管理页面特定状态（滚动位置、展开行、选中项等）
 * 3. 管理标签页生命周期（激活、失活、关闭）
 * 
 * 注意：
 * - 分页、筛选、排序状态由 useTableQuery Hook 通过 URL 管理
 * - 标签页状态管理专注于页面特定的用户体验状态
 */
class TabsManagerInstance {
  private static instance: TabsManagerInstance;
  private pageStateCache: Map<string, TabState> = new Map();
  private lifecycleHandlers: Map<string, TabLifecycleConfig> = new Map();
  private activeTabId: string | null = null;

  private constructor() {}

  static getInstance(): TabsManagerInstance {
    if (!TabsManagerInstance.instance) {
      TabsManagerInstance.instance = new TabsManagerInstance();
    }
    return TabsManagerInstance.instance;
  }

  // 注册标签页生命周期处理器
  registerLifecycleHandler(tabId: string, config: TabLifecycleConfig): void {
    this.lifecycleHandlers.set(tabId, config);
  }

  // 注销标签页生命周期处理器
  unregisterLifecycleHandler(tabId: string): void {
    this.lifecycleHandlers.delete(tabId);
  }

  // 保存标签页状态
  saveTabState(tabId: string, state: any): void {
    const config = this.lifecycleHandlers.get(tabId);
    
    // 构建页面状态
    let pageState: TabState;
    
    // 检查状态是否真的发生了变化
    const existingState = this.pageStateCache.get(tabId);
    const stateChanged = !existingState || JSON.stringify(existingState) !== JSON.stringify(state);
    
    // 安全地获取现有状态的元数据
    const existingAccessCount = existingState?.metadata?.accessCount || 0;
    const existingCreatedAt = existingState?.metadata?.createdAt || Date.now();
    
    if (config?.onSaveState) {
      // 调用自定义保存逻辑
      const customState = config.onSaveState();
      pageState = {
        pageState: {
          scrollPosition: 0,
          expandedRows: [],
          selectedItems: [],
          customState
        },
        persistentState: {
          userPreferences: {},
          lastViewSettings: {}
        },
        metadata: {
          lastAccessTime: Date.now(),
          accessCount: existingAccessCount + 1,
          isDirty: stateChanged,
          createdAt: existingCreatedAt
        }
      };
    } else {
      // 使用默认保存逻辑
      pageState = {
        pageState: {
          scrollPosition: 0,
          expandedRows: [],
          selectedItems: [],
          customState: state
        },
        persistentState: {
          userPreferences: {},
          lastViewSettings: {}
        },
        metadata: {
          lastAccessTime: Date.now(),
          accessCount: existingAccessCount + 1,
          isDirty: stateChanged,
          createdAt: existingCreatedAt
        }
      };
    }
    
    // 保存到内存缓存
    this.pageStateCache.set(tabId, pageState);
    
    // 如果需要持久化到sessionStorage
    if (config?.persistToStorage) {
      const storageKey = config.customStorageKey || `tab_state_${tabId}`;
      storage.setItem(storageKey, pageState);
      console.log(`💾 标签页状态已保存到sessionStorage: ${storageKey}`);
    } else {
      console.log(`💾 标签页状态已保存到内存缓存: ${tabId}`);
    }
  }

  // 恢复标签页状态
  restoreTabState(tabId: string): any {
    const config = this.lifecycleHandlers.get(tabId);
    
    // 首先尝试从内存缓存恢复
    let state = this.pageStateCache.get(tabId);
    
    // 如果内存中没有，且配置了持久化，则从sessionStorage恢复
    if (!state && config?.persistToStorage) {
      const storageKey = config.customStorageKey || `tab_state_${tabId}`;
      const stored = storage.getItem(storageKey);
      if (stored) {
        state = stored;
        // 恢复到内存缓存
        this.pageStateCache.set(tabId, stored);
        console.log(`🔄 从sessionStorage恢复状态: ${storageKey}`);
      }
    }
    
    if (state && config?.onRestoreState) {
      // 调用自定义恢复逻辑
      config.onRestoreState(state);
    }
    
    return state;
  }

  // 清理标签页状态
  clearTabState(tabId: string): void {
    const config = this.lifecycleHandlers.get(tabId);
    
    // 从内存缓存中移除
    this.pageStateCache.delete(tabId);
    
    // 如果需要清理sessionStorage
    if (config?.persistToStorage) {
      const storageKey = config.customStorageKey || `tab_state_${tabId}`;
      storage.removeItem(storageKey);
    }
    
    // 调用自定义清理逻辑
    if (config?.onClearState) {
      config.onClearState();
    }
  }

  // 设置当前活动标签页
  setActiveTab(tabId: string): void {
    this.activeTabId = tabId;
  }

  // 获取当前活动标签页
  getActiveTab(): string | null {
    return this.activeTabId;
  }

  // 检查标签页是否有生命周期配置
  hasLifecycleConfig(tabId: string): boolean {
    return this.lifecycleHandlers.has(tabId);
  }

  // 获取标签页的生命周期配置
  getLifecycleConfig(tabId: string): TabLifecycleConfig | undefined {
    return this.lifecycleHandlers.get(tabId);
  }

  // 触发标签页激活事件
  triggerTabActivate(tabId: string): void {
    const config = this.lifecycleHandlers.get(tabId);
    if (config?.onTabActivate) {
      config.onTabActivate();
    }
  }

  // 触发标签页失活事件
  triggerTabDeactivate(tabId: string): void {
    const config = this.lifecycleHandlers.get(tabId);
    if (config?.onTabDeactivate) {
      config.onTabDeactivate();
    }
  }

  // 触发标签页关闭事件
  triggerTabClose(tabId: string): void {
    const config = this.lifecycleHandlers.get(tabId);
    if (config?.onTabClose) {
      config.onTabClose();
    }
  }

  // 新增：智能状态管理方法
  
  // 保存标签页完整状态
  saveTabFullState(tabId: string, state: Partial<TabState>): void {
    const existingState = this.pageStateCache.get(tabId);
    
    // 安全地获取现有状态的元数据
    const existingAccessCount = existingState?.metadata?.accessCount || 0;
    const existingCreatedAt = existingState?.metadata?.createdAt || Date.now();
    
    const updatedState: TabState = {
      pageState: {
        scrollPosition: 0,
        expandedRows: [],
        selectedItems: [],
        customState: {}
      },
      persistentState: {
        userPreferences: {},
        lastViewSettings: {}
      },
      metadata: {
        lastAccessTime: Date.now(),
        accessCount: existingAccessCount + 1,
        isDirty: true,
        createdAt: existingCreatedAt
      },
      ...state
    };
    
    this.pageStateCache.set(tabId, updatedState);
    console.log(`💾 保存标签页完整状态: ${tabId}`, updatedState);
  }

  // 恢复标签页完整状态
  restoreTabFullState(tabId: string): TabState | null {
    const state = this.pageStateCache.get(tabId);
    if (state) {
      // 安全地更新访问统计
      if (state.metadata) {
        state.metadata.lastAccessTime = Date.now();
        state.metadata.accessCount = (state.metadata.accessCount || 0) + 1;
      } else {
        // 如果metadata不存在，创建默认的metadata
        state.metadata = {
          lastAccessTime: Date.now(),
          accessCount: 1,
          isDirty: false,
          createdAt: Date.now()
        };
      }
      
      this.pageStateCache.set(tabId, state);
      console.log(`🔄 恢复标签页完整状态: ${tabId}`, state);
      return state;
    } else {
      console.log(`ℹ️ 标签页没有保存的状态: ${tabId}`);
      return null;
    }
  }

  // 检查标签页是否有状态
  hasTabState(tabId: string): boolean {
    return this.pageStateCache.has(tabId);
  }

  // 获取标签页状态
  getTabState(tabId: string): TabState | null {
    return this.pageStateCache.get(tabId) || null;
  }

  // 更新标签页状态
  updateTabState(tabId: string, updates: Partial<TabState>): void {
    const existingState = this.pageStateCache.get(tabId);
    if (existingState) {
      const updatedState = { ...existingState, ...updates };
      // 安全地更新元数据
      if (updatedState.metadata) {
        updatedState.metadata.lastAccessTime = Date.now();
        updatedState.metadata.isDirty = true;
      } else {
        // 如果metadata不存在，创建默认的metadata
        updatedState.metadata = {
          lastAccessTime: Date.now(),
          accessCount: 1,
          isDirty: true,
          createdAt: Date.now()
        };
      }
      
      this.pageStateCache.set(tabId, updatedState);
      console.log(`📝 更新标签页状态: ${tabId}`, updates);
    } else {
      console.warn(`⚠️ 尝试更新不存在的标签页状态: ${tabId}`);
    }
  }

  // 清理过期状态
  cleanupExpiredStates(expiryTime: number = 30 * 60 * 1000): void {
    const now = Date.now();
    let cleanedCount = 0;
    
    Array.from(this.pageStateCache.entries()).forEach(([tabId, state]) => {
      if (now - state.metadata.lastAccessTime > expiryTime) {
        this.pageStateCache.delete(tabId);
        cleanedCount++;
      }
    });
    
    if (cleanedCount > 0) {
      console.log(`🧹 清理过期状态: ${cleanedCount} 个标签页`);
    }
  }
}

// 导出全局实例
export const tabsManager = TabsManagerInstance.getInstance();

const TabsManager: React.FC<TabsManagerProps> = ({ onTabChange }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    isAdmin,
    isHQStaff,
    isWarehouseSupervisor,
    isWarehouseStaff,
    isMainAccount,
    isSubAccount
  } = usePermissions();
  const [tabs, setTabs] = useState<EnhancedTabItem[]>([]);
  const [activeKey, setActiveKey] = useState<string>('');
  const [showBatchMenu, setShowBatchMenu] = useState<boolean>(false);
  const [batchMenuPos, setBatchMenuPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [ctxVisible, setCtxVisible] = useState<boolean>(false);
  const [ctxPos, setCtxPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [ctxBaseKey, setCtxBaseKey] = useState<string>('');
  const initialized = useRef(false);
  const previousActiveKey = useRef<string>('');
  const currentUserId = useRef<string>('');

  // 获取标签页标题
  const getTabTitle = (path: string): string => {
    const pathSegments = path.split('/').filter(Boolean);
    // 子账号管理（管理员）：入口与客户定向页
    if (
      path.startsWith('/dashboard/admin/customers/sub-accounts') ||
      (path.startsWith('/dashboard/admin/customers/') && path.endsWith('/sub-accounts'))
    ) {
      return t('tab.subAccountManagement');
    }

    // 内容管理动态路由
    if (pathSegments.includes('content') && pathSegments.includes('edit')) {
      return t('tab.contentEdit');
    }
    if (pathSegments.includes('content') && pathSegments.includes('preview')) {
      return t('tab.contentPreview');
    }

    
    console.log('🔍 生成标签页标题:', { path, pathSegments });

    // 优先处理：项目基础配置创建页
    // 形如：/dashboard/(customer|admin|staff)/projects/inventory-config/create
    if (
      pathSegments.includes('projects') &&
      pathSegments.includes('inventory-config') &&
      pathSegments[pathSegments.length - 1] === 'create'
    ) {
      // 使用i18n键，缺失则回退中文
      const localized = t('tab.projectBasicConfigCreate', { defaultValue: '创建项目基础配置' });
      return localized || '创建项目基础配置';
    }
    
    // 首先尝试完整路径映射
    if (tabTitleMap[path]) {
      console.log('✅ 使用完整路径映射:', tabTitleMap[path]);
    return t(tabTitleMap[path]);
    }
    
    // 处理动态路由
    // 项目基础配置编辑：/dashboard/(customer|admin|staff)/projects/inventory-config/:id/edit
    if (
      pathSegments.includes('projects') &&
      pathSegments.includes('inventory-config') &&
      pathSegments[pathSegments.length - 1] === 'edit'
    ) {
      const basePath = path.split('?')[0];
      const codeFromEditPath = sessionStorage.getItem(`tab_title_code:${basePath}`);
      const detailPath = basePath.replace(/\/edit$/, '');
      const codeFromDetailPath = sessionStorage.getItem(`tab_title_code:${detailPath}`);
      const projectCode = codeFromEditPath || codeFromDetailPath || '';
      const editTitle = t('tab.projectBasicConfigEdit', { defaultValue: '编辑项目基础配置' });
      return projectCode ? `${editTitle}-${projectCode}` : editTitle;
    }

    // 项目基础配置详情：/dashboard/(customer|admin|staff)/projects/inventory-config/:id
    if (pathSegments.includes('projects') && pathSegments.includes('inventory-config')) {
      const idSegment = pathSegments[pathSegments.length - 1];
      const id = Number(idSegment);
      if (!Number.isNaN(id)) {
        // 优先从 sessionStorage 使用基于 path 的缓存
        const basePath = path.split('?')[0];
        const sysCode = sessionStorage.getItem(`tab_title_code:${basePath}`);
        if (sysCode) {
          return `${t('tab.projectBasicConfig')}-${sysCode}`;
        }
        // 无缓存时显示占位，避免仅显示ID
        return t('tab.projectBasicConfig');
      }
    }

    // 仓库编辑：/dashboard/admin/warehouses/:id/edit
    if (pathSegments.includes('warehouses') && pathSegments[pathSegments.length - 1] === 'edit') {
      const idSeg = pathSegments[pathSegments.length - 2];
      const idNum = Number(idSeg);
      const basePath = path.split('?')[0];
      const cachedName = sessionStorage.getItem(`tab_title_warehouse_edit:${basePath}`);
      if (cachedName) {
        return `${t('tab.warehousesEdit')}-${cachedName}`;
      }
      const detailBase = basePath.replace(/\/edit$/, '');
      const detailName = sessionStorage.getItem(`tab_title_warehouse:${detailBase}`);
      if (detailName) {
        return `${t('tab.warehousesEdit')}-${detailName}`;
      }
      if (!Number.isNaN(idNum)) {
        return `${t('tab.warehousesEdit')}-${idNum}`;
      }
      return t('tab.warehousesEdit');
    }

    // 仓库详情：/dashboard/(admin|customer|staff)/warehouses/:id 或 /dashboard/warehouses/:id
    if (pathSegments.includes('warehouses')) {
      const last = pathSegments[pathSegments.length - 1];
      const id = Number(last);
      if (!Number.isNaN(id)) {
        const basePath = path.split('?')[0];
        const name = sessionStorage.getItem(`tab_title_warehouse:${basePath}`);
        if (name) {
          return `${t('tab.warehousesDetail')}-${name}`;
        }
        // 无缓存时回退到通用标题
        return t('tab.warehousesDetail');
      }
    }

    if (pathSegments.includes('projects') && pathSegments.includes('address')) {
      return t('tab.idAddress');
    }

    // 处理入库标签页面（兼容 label/labels）
    if (pathSegments.includes('forecast') && (pathSegments.includes('labels') || pathSegments.includes('label'))) {
      const basePath = path.split('?')[0];
      const code = sessionStorage.getItem(`tab_title_inbound_forecast_label:${basePath}`);
      if (code) return `${t('tab.inboundForecastLabel')} - ${code}`;
      return t('tab.inboundForecastLabel');
    }

    // 处理编辑产品页面：/products/:id/edit
    if (pathSegments.includes('products') && pathSegments[pathSegments.length - 1] === 'edit') {
      const basePath = path.split('?')[0];
      const code = sessionStorage.getItem(`tab_title_product_edit:${basePath}`);
      return code ? `${t('tab.productsEdit')}-${code}` : t('tab.productsEdit');
    }

    // 处理库存单元标签页面
    if (pathSegments.includes('inventory-config') && pathSegments.includes('labels')) {
      return t('tab.inventoryConfigLabelsCustomer');
    }

    // 处理入库预报详情页面
    const lastSegment = pathSegments[pathSegments.length - 1];
    if (pathSegments.includes('forecast') && !pathSegments.includes('label')) {
      const isNumericId = !isNaN(Number(lastSegment));
      const isPrefixedId = /^f-\d+$/i.test(String(lastSegment || ''));
      if (isNumericId || isPrefixedId) {
        const basePath = path.split('?')[0];
        const code = sessionStorage.getItem(`tab_title_inbound_forecast:${basePath}`);
        if (code) return `${t('tab.inboundForecastDetail')}-${code}`;
        let idNum = Number(lastSegment);
        if (Number.isNaN(idNum)) {
          const m = String(lastSegment || '').match(/^f-(\d+)$/i);
          if (m && m[1]) idNum = Number(m[1]);
        }
        if (!Number.isNaN(idNum)) return `${t('tab.inboundForecastDetail')}-${idNum}`;
        return t('tab.inboundForecastDetail');
      }
    }
    
    // 处理入库记录详情页面
    if (pathSegments.includes('records')) {
      let idNum = Number(lastSegment);
      if (Number.isNaN(idNum)) {
        const m = String(lastSegment || '').match(/^r-(\d+)$/i);
        if (m && m[1]) idNum = Number(m[1]);
      }
      if (!Number.isNaN(idNum)) {
        const basePath = path.split('?')[0];
        const code = sessionStorage.getItem(`tab_title_inbound_record:${basePath}`);
        if (code) return `${t('tab.inboundRecordDetail')}-${code}`;
        return `${t('tab.inboundRecordDetail')}-${idNum}`;
      }
    }

    // 指令编辑：/dashboard/(customer|admin|staff)/instructions/:id/edit
    if (pathSegments.includes('instructions') && pathSegments[pathSegments.length - 1] === 'edit') {
      const idSeg = pathSegments[pathSegments.length - 2];
      const idNum = Number(idSeg);
      const basePath = path.split('?')[0];
      const codeEdit = sessionStorage.getItem(`tab_title_instruction_edit:${basePath}`);
      if (!Number.isNaN(idNum)) {
        if (codeEdit) return `指令修改-${codeEdit}`;
        // 兼容：从详情的缓存读取
        const detailBase = basePath.replace(/\/edit$/, '');
        const codeFromDetail = sessionStorage.getItem(`tab_title_instruction:${detailBase}`);
        if (codeFromDetail) return `指令修改-${codeFromDetail}`;
        return `指令修改-${idNum}`;
      }
      return '指令修改';
    }

    // 工单模板编辑：/dashboard/admin/work-orders/templates/:id/edit
    if (path.includes('/dashboard/admin/work-orders/templates/') && path.endsWith('/edit')) {
      return t('tab.workOrderTemplateEdit');
    }

    // 指令详情：/dashboard/(customer|admin|staff)/instructions/:id
    if (pathSegments.includes('instructions') && pathSegments.includes('batch')) {
      const last = pathSegments[pathSegments.length - 1];
      const id = Number(last);
      if (!Number.isNaN(id)) {
        const basePath = path.split('?')[0];
        const code = sessionStorage.getItem(`tab_title_batch:${basePath}`);
        if (code) return `批次详情-${code}`;
        return `批次详情-${id}`;
      }
    }
    if (pathSegments.includes('instructions')) {
      const last = pathSegments[pathSegments.length - 1];
      let id = Number(last);
      if (Number.isNaN(id)) {
        const m = String(last || '').match(/^i-(\d+)$/i);
        if (m && m[1]) id = Number(m[1]);
      }
      if (!Number.isNaN(id)) {
        const basePath = path.split('?')[0];
        const code = sessionStorage.getItem(`tab_title_instruction:${basePath}`);
        if (code) return `指令详情-${code}`;
        return `指令详情-${id}`;
      }
    }
    
    // 执行页（指令执行详情）：/dashboard/(admin|staff)/execution/instruction/:id
    if (pathSegments.includes('execution') && pathSegments.includes('instruction')) {
      const last = pathSegments[pathSegments.length - 1];
      const idNum = Number(last);
      if (!Number.isNaN(idNum)) {
        return t('tab.instructionExecute');
      }
    }
    // 执行页（批次执行详情）：/dashboard/(admin|staff)/execution/batch/:id
    if (pathSegments.includes('execution') && pathSegments.includes('batch')) {
      const last = pathSegments[pathSegments.length - 1];
      const idNum = Number(last);
      if (!Number.isNaN(idNum)) {
        return t('tab.batchExecute', { defaultValue: `批次执行-${idNum}` });
      }
    }

    // 尝试从映射中获取标题（最后两段）
    const lastTwoSegments = pathSegments.slice(-2).join('-');
    
    if (tabTitleMap[lastTwoSegments]) {
      return t(tabTitleMap[lastTwoSegments]);
    }

    // 尝试从映射中获取标题（最后三段）
    const lastThreeSegments = pathSegments.slice(-3).join('-');
    
    if (tabTitleMap[lastThreeSegments]) {
      return t(tabTitleMap[lastThreeSegments]);
    }
    
    // 特殊处理：入库记录列表页
    if (pathSegments.includes('inbound') && pathSegments.includes('records') && !/\d+$/.test(path)) {
      console.log('✅ 使用入库记录列表页特殊处理');
      return t('tab.inboundRecords');
    }
    
    // 特殊处理：库存管理页面
    if (pathSegments.includes('inventory') && pathSegments.includes('current')) {
      console.log('✅ 使用当前库存页面特殊处理');
      return t('tab.currentInventory');
    }
    
    if (pathSegments.includes('inventory') && pathSegments.includes('history')) {
      console.log('✅ 使用库存历史页面特殊处理');
      return t('tab.inventoryHistory');
    }
    
    // 如果没有映射，则使用路径的最后一段
  const finalTitle = t(tabTitleMap[lastSegment]) || lastSegment || t('tab.dashboard');
    console.log('✅ 最终标题:', finalTitle);
    return finalTitle;
  };

  // 语言切换时无刷新重算所有标签标题
  useEffect(() => {
    setTabs(prev => prev.map(tab => ({
      ...tab,
      title: getTabTitle(tab.path),
    })));
  // 仅依赖语言与路径（确保在路由变化或语言变化时都能重算）
  }, [i18n.language, location.pathname]);

  // 详情页拿到系统编码后，定向刷新当前路径标签标题（避免首次仅显示ID）
  useEffect(() => {
    const handler = (evt: Event) => {
      const customEvt = evt as CustomEvent<{ path?: string }>;
      const targetPath = String(customEvt?.detail?.path || '').trim();
      if (!targetPath) return;
      setTabs(prev => prev.map(tab => {
        if (tab.path !== targetPath) return tab;
        const nextTitle = getTabTitle(tab.path);
        if (tab.title === nextTitle) return tab;
        return { ...tab, title: nextTitle };
      }));
    };
    window.addEventListener('dashboard-tab-title-updated', handler as EventListener);
    return () => {
      window.removeEventListener('dashboard-tab-title-updated', handler as EventListener);
    };
  }, [i18n.language, location.pathname]);

  // 获取首页标签的key和路径
  const getHomeTabInfo = () => {
    // 优先依据当前路径判断，避免权限尚未就绪时误判为 customer
    const p = location.pathname;
    if (p.startsWith('/dashboard/admin')) {
      return { key: 'admin-dashboard-home', path: '/dashboard/admin' };
    }
    if (p.startsWith('/dashboard/staff')) {
      return { key: 'staff-dashboard-home', path: '/dashboard/staff' };
    }
    if (p.startsWith('/dashboard/customer')) {
      return { key: 'customer-dashboard-home', path: '/dashboard/customer' };
    }

    // 其次依据角色判断
    if (isAdmin()) {
      return { key: 'admin-dashboard-home', path: '/dashboard/admin' };
    }
    if (isHQStaff() || isWarehouseSupervisor() || isWarehouseStaff()) {
      return { key: 'staff-dashboard-home', path: '/dashboard/staff' };
    }
    if (isMainAccount() || isSubAccount()) {
      return { key: 'customer-dashboard-home', path: '/dashboard/customer' };
    }
    // 未知：默认客户
    return { key: 'customer-dashboard-home', path: '/dashboard/customer' };
  };

  // 智能标签页管理
  
  // 检查是否已有相同路径的标签页（修复：支持tabId查找和状态参数匹配）
  const findExistingTab = (path: string): EnhancedTabItem | null => {
    console.log('🔍 查找现有标签页:', { path, currentTabs: tabs.length });
    
    // 首先尝试从当前URL中获取tabId
    const currentUrl = new URL(window.location.href);
    const currentTabId = currentUrl.searchParams.get('tabId');
    
    // 如果有tabId，优先按tabId查找
    if (currentTabId) {
      const tabIdMatch = tabs.find(tab => tab.key === currentTabId);
      if (tabIdMatch) {
        // 重要：tabId 可能是“上一个页面遗留”的 query（例如通过菜单跳转时未清理 tabId）
        // 仅当 tabId 对应的标签页路径与当前目标 path 匹配时，才认为是有效命中；否则会导致“内容已切换但高亮没变”
        const norm = (p: string) => String(p || '').replace(/\/\d+/g, '/:id');
        const ok = (tabIdMatch.path === path) || (norm(tabIdMatch.path) === norm(path));
        if (ok) {
          console.log('✅ 按tabId找到标签页(且路径匹配):', tabIdMatch.key, '路径:', tabIdMatch.path);
          return tabIdMatch;
        }
        console.warn('⚠️ tabId 命中但路径不匹配，忽略该命中以避免高亮错乱:', {
          currentTabId,
          tabPath: tabIdMatch.path,
          targetPath: path,
        });
      }
    }
    
    // 然后检查是否有完全相同的路径
    const exactMatch = tabs.find(tab => tab.path === path);
    if (exactMatch) {
      console.log('✅ 找到完全匹配的标签页:', exactMatch.key, '路径:', exactMatch.path);
      return exactMatch;
    }
    
    // 检查是否有相同基础路径的标签页（排除动态参数）
    const basePath = path.replace(/\/\d+/g, '/:id');
    const baseMatch = tabs.find(tab => {
      const tabBasePath = tab.path.replace(/\/\d+/g, '/:id');
      return tabBasePath === basePath;
    });
    
    if (baseMatch) {
      console.log('✅ 找到基础路径匹配的标签页:', baseMatch.key, '路径:', baseMatch.path);
      return baseMatch;
    }
    
    console.log('❌ 未找到匹配的标签页，需要创建新的');
    return null;
  };

  // 智能创建或切换标签页（修复：改进状态恢复逻辑，确保状态参数保持）
  const smartCreateOrSwitchTab = (path: string): void => {
    console.log('🧠 智能标签页管理:', { path, currentTabs: tabs.length });
    
    const existingTab = findExistingTab(path);
    
    if (existingTab) {
      // 已有标签页：切换到该标签页，保持状态
      console.log('✅ 切换到已存在的标签页:', existingTab.key, '路径:', existingTab.path);
      setActiveKey(existingTab.key);
      tabsManager.setActiveTab(existingTab.key);
      
      // 恢复标签页状态（只恢复页面特定状态，不处理URL状态）
      restoreTabState(existingTab.key);
      
      // 如果路径不同，更新标签页路径（处理动态参数变化）
      if (existingTab.path !== path) {
        console.log('🔄 更新标签页路径:', existingTab.path, '->', path);
        setTabs(prev => prev.map(tab => 
          tab.key === existingTab.key ? { ...tab, path } : tab
        ));
      }
      
      // 确保URL包含正确的tabId和状态参数
      const currentUrl = new URL(window.location.href);
      const currentTabId = currentUrl.searchParams.get('tabId');
      
      console.log('🔍 检查URL状态:', {
        currentUrl: currentUrl.href,
        currentTabId,
        existingTabKey: existingTab.key,
        path,
        currentSearchParams: Array.from(currentUrl.searchParams.entries())
      });
      
      if (currentTabId !== existingTab.key) {
        // 如果URL中的tabId不匹配，更新URL但保持所有状态参数
        console.log('🔄 更新URL中的tabId:', currentTabId, '->', existingTab.key);
        
        // 尝试从sessionStorage恢复完整状态
        const savedUrlState = storage.getItem(`tab_url_state_${existingTab.key}`);
        console.log('💾 检查sessionStorage状态:', {
          key: `tab_url_state_${existingTab.key}`,
          exists: !!savedUrlState,
          content: savedUrlState
        });
        
        if (savedUrlState) {
          const urlState = savedUrlState;
            console.log('🔄 从sessionStorage恢复完整状态:', urlState);
            
            const urlWithState = buildUrlWithState(path, existingTab.key, urlState);
            console.log('🔗 构建包含完整状态的URL:', urlWithState);
            
            // 使用replace模式导航，避免在历史记录中创建重复条目
            // 这是唯一需要导航的地方，确保URL包含正确的状态
            console.log('🔄 导航到包含状态的URL');
            navigate(urlWithState, { replace: true });
            
            // 等待导航完成后，确保useTableQuery能正确解析状态
            setTimeout(() => {
              console.log('⏳ 导航完成，检查URL状态:', window.location.href);
              
              // 强制触发useTableQuery的状态重新解析
              // 通过触发一个小的URL变化来确保状态同步
              const currentUrl = new URL(window.location.href);
              if (currentUrl.searchParams.get('tabId') === existingTab.key) {
                console.log('✅ 导航成功，tabId已正确设置');
                
                // 检查是否有状态参数
                const hasStateParams = Array.from(currentUrl.searchParams.entries()).some(([key, value]) => 
                  key !== 'tabId' && (key.includes('_page') || key.includes('_filter_') || key.includes('_sort'))
                );
                
                if (hasStateParams) {
                  console.log('✅ URL包含状态参数，状态应该能正确恢复');
                } else {
                  console.log('⚠️ URL缺少状态参数，可能需要手动恢复');
                }
              } else {
                console.log('❌ 导航失败，tabId未正确设置');
              }
            }, 100);
            

        } else {
          // 如果没有保存的状态，使用buildTabUrl保持当前URL参数
          console.log('ℹ️ 没有保存的状态，保持当前URL参数');
          const urlWithParams = buildTabUrl(path, existingTab.key);
          console.log('🔗 构建保持参数的URL:', urlWithParams);
          navigate(urlWithParams, { replace: true });
        }
      } else {
        console.log('✅ URL中的tabId已匹配，无需更新');
        
        // 即使tabId匹配，也要检查是否有状态参数需要恢复
        const savedUrlState = storage.getItem(`tab_url_state_${existingTab.key}`);
        if (savedUrlState) {
          const urlState = savedUrlState;
            console.log('🔄 检查是否需要恢复保存的状态:', urlState);
            
            // 检查当前URL是否包含状态参数
            const currentUrl = new URL(window.location.href);
            const hasStateParams = Array.from(currentUrl.searchParams.entries()).some(([key, value]) => 
              key !== 'tabId' && (key.includes('_page') || key.includes('_filter_') || key.includes('_sort'))
            );
            
            if (!hasStateParams) {
              console.log('🔄 当前URL缺少状态参数，恢复保存的状态');
              const urlWithState = buildUrlWithState(path, existingTab.key, urlState);
              navigate(urlWithState, { replace: true });
            } else {
              console.log('✅ 当前URL已包含状态参数，无需恢复');
            }

        }
      }
    } else {
      // 新标签页：创建并初始化
      console.log('✅ 创建新标签页:', path);
      createNewTab(path);
    }
  };

  // 构建包含tabId和状态参数的完整URL
  const buildTabUrl = useCallback((path: string, tabId: string): string => {
    // 从当前URL获取状态参数（分页、筛选、排序等）
    const currentUrl = new URL(window.location.href);
    const searchParams = new URLSearchParams();
    
    // 添加tabId参数
    searchParams.set('tabId', tabId);
    
    // 保持当前URL中的所有状态参数（分页、筛选、排序等）
    let preservedCount = 0;
    let stateParamsCount = 0;
    
    currentUrl.searchParams.forEach((value, key) => {
      if (key !== 'tabId') {
        // 保持所有非tabId的参数
        searchParams.set(key, value);
        preservedCount++;
        
        // 检查是否是状态参数（分页、筛选、排序）
        if (key.includes('_page') || key.includes('_filter_') || key.includes('_sort') || 
            key === 'page' || key === 'pageSize' || key.startsWith('filter_') || 
            key === 'sortField' || key === 'sortOrder') {
          stateParamsCount++;
        }
      }
    });
    
    const searchString = searchParams.toString();
    const fullUrl = searchString ? `${path}?${searchString}` : path;
    
    console.log('🔗 构建标签页URL:', { 
      path, 
      tabId, 
      fullUrl,
      preservedParams: Array.from(searchParams.entries()),
      preservedCount,
      stateParamsCount,
      originalUrl: currentUrl.href
    });
    
    // 验证状态参数是否保持
    if (preservedCount > 0) {
      console.log('✅ 成功保持参数:', preservedCount, '个');
      if (stateParamsCount > 0) {
        console.log('✅ 其中状态参数:', stateParamsCount, '个');
      }
    } else {
      console.log('⚠️ 没有参数需要保持');
    }
    
    return fullUrl;
  }, []);

  // 构建包含完整状态的URL
  const buildUrlWithState = useCallback((path: string, tabId: string, urlState: any): string => {
    const searchParams = new URLSearchParams();
    
    // 添加tabId参数
    searchParams.set('tabId', tabId);
    
    console.log('🔨 开始构建URL状态:', { path, tabId, urlState });
    
    // 添加分页参数
    if (urlState.pagination) {
      if (urlState.pagination.current && urlState.pagination.current !== 1) {
        searchParams.set(`${tabId}_page`, urlState.pagination.current.toString());
        console.log('📄 添加分页参数:', `${tabId}_page = ${urlState.pagination.current}`);
      }
      if (urlState.pagination.pageSize && urlState.pagination.pageSize !== 10) {
        searchParams.set(`${tabId}_pageSize`, urlState.pagination.pageSize.toString());
        console.log('📄 添加分页参数:', `${tabId}_pageSize = ${urlState.pagination.pageSize}`);
      }
    }
    
    // 添加筛选参数
    if (urlState.filters) {
      console.log('🔍 处理筛选参数:', urlState.filters);
      Object.entries(urlState.filters).forEach(([key, value]) => {
        // 修改：让false值也能添加到URL，确保"常规入库"等状态能正确保持
        if (value !== undefined && value !== null && value !== '') {
          // 包括false、0等"假值"，但不包括undefined、null和空字符串
          const paramKey = `${tabId}_filter_${key}`;
          const paramValue = value.toString();
          searchParams.set(paramKey, paramValue);
          console.log('🔍 添加筛选参数:', `${paramKey} = ${paramValue}`);
        }
      });
    }
    
    // 添加排序参数
    if (urlState.sorting) {
      if (urlState.sorting.field && urlState.sorting.field !== 'created_at') {
        searchParams.set(`${tabId}_sortField`, urlState.sorting.field);
        console.log('📊 添加排序参数:', `${tabId}_sortField = ${urlState.sorting.field}`);
      }
      if (urlState.sorting.order && urlState.sorting.order !== 'desc') {
        searchParams.set(`${tabId}_sortOrder`, urlState.sorting.order);
        console.log('📊 添加排序参数:', `${tabId}_sortOrder = ${urlState.sorting.order}`);
      }
    }
    
    const searchString = searchParams.toString();
    const fullUrl = searchString ? `${path}?${searchString}` : path;
    
    console.log('🔗 构建包含状态的URL:', { 
      path, 
      tabId, 
      urlState,
      fullUrl,
      paramCount: searchParams.size,
      searchParams: Array.from(searchParams.entries())
    });
    
    return fullUrl;
  }, []);

  // 恢复标签页状态（修复：只恢复页面特定状态，不处理URL状态）
  const restoreTabState = useCallback((tabKey: string) => {
    console.log('🔄 恢复标签页状态:', tabKey);
    
    try {
      const tabState = tabsManager.getTabState(tabKey);
      if (!tabState) {
        console.log('❌ 标签页状态不存在:', tabKey);
        return;
      }
      
      console.log('📋 标签页状态详情:', tabState);
      
      // 恢复页面特定状态（滚动位置、展开行等）
      if (tabState.pageState) {
        console.log('🔄 恢复页面状态:', tabState.pageState);
        // 这里可以添加页面特定状态的恢复逻辑
        // 例如：滚动位置、展开行、表单状态等
      }
      
      // 恢复持久化状态（用户偏好等）
      if (tabState.persistentState) {
        console.log('🔄 恢复持久化状态:', tabState.persistentState);
        // 这里可以添加持久化状态的恢复逻辑
        // 例如：用户偏好设置、主题等
      }
      
      // 注意：不在这里处理URL状态（分页、筛选、排序）
      // URL状态由 useTableQuery Hook 通过 URL 参数自动恢复
      // 这样可以避免双重导航和状态冲突
      
      // 更新访问统计
      tabsManager.updateTabState(tabKey, {
        metadata: {
          ...tabState.metadata,
          lastAccessTime: Date.now(),
          accessCount: (tabState.metadata?.accessCount || 0) + 1
        }
      });
      
      console.log('✅ 标签页状态恢复完成:', tabKey);
      
    } catch (error) {
      console.error('❌ 恢复标签页状态失败:', error);
    }
  }, [tabs, navigate, buildUrlWithState]);

  // 创建新标签页
  const createNewTab = (path: string): void => {
    const [pathOnly, rawQuery = ''] = String(path || '').split('?');
    const incomingParams = new URLSearchParams(rawQuery);
    // 生成唯一的tabId
    const tabId = createUniqueTabId(pathOnly);
    const newTabKey = tabId; // 使用tabId作为key
    const newTabTitle = getTabTitle(pathOnly);
    
    const newTab: EnhancedTabItem = {
      key: newTabKey,
      title: newTabTitle,
      path: pathOnly,
      closable: true,
      config: getTabConfig(pathOnly), // 获取标签页配置
    };
    
    // 初始化标签页页面特定状态（滚动位置、展开行、选中项等）
    // 注意：分页、筛选、排序状态由 useTableQuery Hook 通过 URL 管理
    const initialTabState: TabState = {
      pageState: {
        scrollPosition: 0,
        expandedRows: [],
        selectedItems: [],
        customState: {}
      },
      persistentState: {
        userPreferences: {},
        lastViewSettings: {}
      },
      metadata: {
        lastAccessTime: Date.now(),
        accessCount: 1,
        isDirty: false,
        createdAt: Date.now()
      }
    };
    
    // 保存初始页面特定状态
    tabsManager.saveTabFullState(newTabKey, initialTabState);
    
    // 添加到标签页列表
    setTabs(prev => [...prev, newTab]);
    
    // 激活新标签页
    setActiveKey(newTabKey);
    tabsManager.setActiveTab(newTabKey);
    
    // 导航到带 tabId 的 URL，并保留当前路由携带的 query（如 from_dashboard/filter_status）
    const mergedParams = new URLSearchParams(location.search || '');
    incomingParams.forEach((value, key) => mergedParams.set(key, value));
    mergedParams.set('tabId', tabId);
    const query = mergedParams.toString();
    const urlWithTabId = query ? `${pathOnly}?${query}` : `${pathOnly}?tabId=${tabId}`;
    navigate(urlWithTabId);
    
    console.log('✅ 新标签页创建完成:', newTab, 'tabId:', tabId);
  };

  // 获取标签页配置
  const getTabConfig = (path: string): TabConfig => {
    // 默认配置
    const defaultConfig: TabConfig = {
      path,
      title: getTabTitle(path),
      stateManagement: {
        persistToStorage: false,
        keepStateStrategy: 'memory',
        persistentFields: ['userPreferences', 'lastViewSettings'],
        clearOnClose: true,
      },
      lifecycle: {}
    };
    
    // 特殊页面配置
    if (path.includes('/inbound/records') && !/\d+$/.test(path)) {
              // 入库记录列表页：保持页面特定状态到sessionStorage
      return {
        ...defaultConfig,
        stateManagement: {
          persistToStorage: true,
          keepStateStrategy: 'hybrid',
          persistentFields: ['userPreferences', 'lastViewSettings', 'scrollPosition', 'expandedRows'],
          clearOnClose: false, // 不清理，保持用户设置
        },
        lifecycle: {
          onStateRestore: (state) => {
            console.log('🔄 入库记录列表页状态恢复:', state);
            // 这里会触发页面组件的页面特定状态恢复（滚动位置、展开行等）
          }
        }
      };
    }
    
    return defaultConfig;
  };

  // 生成唯一的标签页Key（修复：确保唯一性）
  const generateKey = (path: string) => {
    console.log('🔑 生成标签页Key:', { path });
    
    // 对于入库记录详情页，生成包含ID的唯一key
    if (path.includes('/inbound/records/') && /\d+$/.test(path)) {
      const id = path.match(/\d+$/)?.[0];
      const key = `inbound-records-detail-${id}`;
      console.log('✅ 入库记录详情页Key:', key);
      return key;
    }
    
    // 对于入库预报详情页，生成包含ID的唯一key
    if (path.includes('/inbound/forecast/') && /\d+$/.test(path) && !path.includes('/label')) {
      const id = path.match(/\d+$/)?.[0];
      const key = `inbound-forecast-detail-${id}`;
      console.log('✅ 入库预报详情页Key:', key);
      return key;
    }
    
    // 生成包含时间戳的唯一key，确保唯一性
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const uniqueKey = `${path.replace(/\/\d+/g, '/:id')}_${timestamp}_${random}`;
    
    console.log('✅ 唯一Key生成:', uniqueKey);
    return uniqueKey;
  };

  // 生成唯一的tabId（用于URL参数隔离）
  const createUniqueTabId = (path: string): string => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 8);
    const tabId = `tab_${timestamp}_${random}`;
    console.log('🔑 生成唯一tabId:', tabId, '路径:', path);
    return tabId;
  };

  // 保存标签页列表到sessionStorage
  const saveTabsToStorage = useCallback((tabsToSave: EnhancedTabItem[]) => {
    const tabsData = tabsToSave.map(tab => ({
      key: tab.key,
      path: tab.path,
      title: tab.title,
      closable: tab.closable,
      config: tab.config
    }));
    
    storage.setItem('dashboard_tabs', tabsData);
    console.log('💾 标签页列表已保存到sessionStorage:', tabsData.length, '个标签页');
  }, []);

  // 从sessionStorage恢复标签页列表
  const restoreTabsFromStorage = useCallback((): EnhancedTabItem[] => {
    const restoredTabs = storage.getItem('dashboard_tabs');
    if (restoredTabs) {
      console.log('🔄 从sessionStorage恢复标签页列表:', restoredTabs.length, '个标签页');
      return restoredTabs;
    }
    return [];
  }, []);

  // 清理过期的标签页状态
  const cleanupExpiredTabs = useCallback(() => {
    const now = Date.now();
    const expiryTime = 24 * 60 * 60 * 1000; // 24小时
    
    setTabs(prev => {
      const validTabs = prev.filter(tab => {
        const tabState = tabsManager.getTabState(tab.key);
        if (tabState && tabState.metadata) {
          const lastAccessTime = tabState.metadata.lastAccessTime || 0;
          if ((now - lastAccessTime) > expiryTime) {
            console.log('🧹 清理过期标签页:', tab.key);
            tabsManager.clearTabState(tab.key);
            return false;
          }
        }
        return true;
      });
      
      if (validTabs.length !== prev.length) {
        console.log('🧹 清理过期标签页完成:', prev.length, '->', validTabs.length);
        saveTabsToStorage(validTabs);
      }
      
      return validTabs;
    });
  }, [saveTabsToStorage]);

  // 清理重复的标签页（新增：防止重复标签页）
  const removeDuplicateTabs = useCallback(() => {
    setTabs(prev => {
      const uniqueTabs = prev.filter((tab, index, array) => {
        // 检查是否有重复的路径
        const firstIndex = array.findIndex(t => t.path === tab.path);
        if (firstIndex !== index) {
          console.log('🧹 发现重复标签页，移除:', tab.key, '路径:', tab.path);
          return false;
        }
        return true;
      });
      
      if (uniqueTabs.length !== prev.length) {
        console.log('🧹 清理重复标签页完成:', prev.length, '->', uniqueTabs.length);
        return uniqueTabs;
      }
      
      return prev;
    });
  }, []);

  // 在标签页列表变化后检查重复
  useEffect(() => {
    if (initialized.current && tabs.length > 0) {
      removeDuplicateTabs();
    }
  }, [tabs, removeDuplicateTabs]);

  // 初始化首页标签（修复：防止重复创建标签页）
  useEffect(() => {
    if (!initialized.current) {
      console.log('🚀 开始初始化标签页管理器');
      
      // 检测用户切换，清理旧数据（使用稳定用户标识，避免 token 刷新误判为换用户）
      const currentToken = localStorage.getItem('token') || '';
      const currentStableId = getStableUserIdFromToken(currentToken);
      const lastStableId = sessionStorage.getItem('dashboard_tabs_user') || '';
      console.log('[TabsManager][Init] user-switch-check', { lastStableIdExists: !!lastStableId, currentStableIdLen: currentStableId.length });
      if (lastStableId && currentStableId && lastStableId !== currentStableId) {
        console.log('🔄 检测到用户切换，清理旧用户的标签页数据');
        // 仅清理当前窗口(sessionStorage)内的标签页数据
        sessionStorage.removeItem('dashboard_tabs');
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('tab_state_') || key.startsWith('tab_url_state_')) {
            sessionStorage.removeItem(key);
            console.log('🧹 清理旧用户标签页状态:', key);
          }
        });
        console.log('✅ 旧用户数据清理完成');
      }
      // 记录当前用户稳定标识到 ref 与 sessionStorage，用于下次判断
      currentUserId.current = currentStableId;
      sessionStorage.setItem('dashboard_tabs_user', currentStableId);
      
      // 尝试从sessionStorage恢复标签页列表
      const restoredTabs = restoreTabsFromStorage();
      
      if (restoredTabs.length > 0) {
        // 恢复标签页列表（同步刷新标题，避免显示旧的 i18n key）
        const normalizedTabs = restoredTabs.map(tab => {
          const title = getTabTitle(tab.path);
          const config = tab.config ? { ...tab.config, title } : tab.config;
          return { ...tab, title, config };
        });
        setTabs(normalizedTabs);
        console.log('🔄 已恢复标签页列表:', normalizedTabs.length, '个标签页');
        
        // 设置当前活动标签页
        const currentTab = normalizedTabs.find(tab => tab.path === location.pathname);
        if (currentTab) {
          setActiveKey(currentTab.key);
          tabsManager.setActiveTab(currentTab.key);
          console.log('✅ 恢复活动标签页:', currentTab.key);
        } else {
          // 如果没有匹配的标签页，创建首页标签
          const homeTabInfo = getHomeTabInfo();
          const homeTab: EnhancedTabItem = {
            key: homeTabInfo.key,
            title: '',
            path: homeTabInfo.path,
            closable: false,
          };
          setTabs(prev => [...prev, homeTab]);
          setActiveKey(homeTabInfo.key);
          tabsManager.setActiveTab(homeTabInfo.key);
          console.log('✅ 创建首页标签:', homeTabInfo.key);
        }
      } else {
        // 创建首页标签
        const homeTabInfo = getHomeTabInfo();
        const homeTab: EnhancedTabItem = {
          key: homeTabInfo.key,
          title: '',
          path: homeTabInfo.path,
          closable: false,
        };
        setTabs([homeTab]);
        setActiveKey(homeTabInfo.key);
        tabsManager.setActiveTab(homeTabInfo.key);
        console.log('✅ 创建首页标签:', homeTabInfo.key);
      }
      
      initialized.current = true;
      console.log('✅ 标签页管理器初始化完成');
      
      // 定期清理过期标签页
      const cleanupInterval = setInterval(cleanupExpiredTabs, 60 * 60 * 1000); // 每小时清理一次
      
      return () => clearInterval(cleanupInterval);
    }
  }, [location.pathname, restoreTabsFromStorage, cleanupExpiredTabs]);

  // 根据当前路径更新标签页（修复：防止重复创建）
  useEffect(() => {
    if (!initialized.current) {
      console.log('⏳ 标签页管理器未初始化，跳过路径更新');
      return;
    }
    
    const currentPath = location.pathname;
    const homeTabInfo = getHomeTabInfo();
    
    console.log('🔄 路径变化，更新标签页:', { currentPath, homePath: homeTabInfo.path });
    
    // 如果是首页路径，直接激活对应的首页标签
    if (currentPath === homeTabInfo.path) {
      console.log('🏠 首页路径，激活首页标签');
      setActiveKey(homeTabInfo.key);
      tabsManager.setActiveTab(homeTabInfo.key);
      return;
    }

    // 从 PermissionRoute 设置的“一次性拦截”标记读取，阻止本次创建
    const denyOnce = sessionStorage.getItem('deny_once_path');
    if (denyOnce && denyOnce === currentPath) {
      console.warn('⛔ 本次路径被权限拒绝，阻止创建标签:', currentPath);
      sessionStorage.removeItem('deny_once_path');
      return;
    }

    // 使用智能标签页管理
    smartCreateOrSwitchTab(currentPath);
  }, [location.pathname]);

  // ✅ 额外同步：当 URL 中存在 tabId（或某些跳转导致 tabId 丢失/滞后）时，确保 activeKey 与当前页面一致
  // 说明：部分页面（如“指令终审”）会在进入后触发 replace/二次导航，可能造成高亮延迟；
  // 这里以 URL.tabId 为准做一次幂等同步，避免必须“再点一次标签页”才高亮。
  useEffect(() => {
    try {
      if (!initialized.current) return;
      const sp = new URLSearchParams(location.search || '');
      const tid = String(sp.get('tabId') || '').trim();
      if (tid) {
        const exists = tabs.some(t => t.key === tid);
        if (exists && activeKey !== tid) {
          setActiveKey(tid);
          tabsManager.setActiveTab(tid);
        }
        return;
      }
      // 若 URL 没有 tabId，则按 pathname 找到对应标签页并同步
      const cur = tabs.find(t => t.path === location.pathname);
      if (cur && activeKey !== cur.key) {
        setActiveKey(cur.key);
        tabsManager.setActiveTab(cur.key);
      }
    } catch {}
  }, [location.search, location.pathname, tabs, activeKey]);

          // 监听标签页列表变化，自动保存到sessionStorage（修复：避免无限循环）
  useEffect(() => {
    if (initialized.current && tabs.length > 0) {
              // 检查是否需要保存（避免重复保存相同内容）
        const currentTabsData = JSON.stringify(tabs.map(tab => ({
          key: tab.key,
          path: tab.path,
          title: tab.title,
          closable: tab.closable,
          config: tab.config
        })));
        
        const lastSavedData = storage.getItem('dashboard_tabs');
        if (lastSavedData !== currentTabsData) {
          console.log('💾 标签页列表有变化，保存到sessionStorage');
          saveTabsToStorage(tabs);
        } else {
          console.log('⏭️ 标签页列表无变化，跳过保存');
        }
    }
  }, [tabs, saveTabsToStorage]);

  // 监听活动标签页变化，触发生命周期事件
  useEffect(() => {
    if (previousActiveKey.current && previousActiveKey.current !== activeKey) {
      // 触发前一个标签页的失活事件
      tabsManager.triggerTabDeactivate(previousActiveKey.current);
    }
    
    if (activeKey) {
      // 触发当前标签页的激活事件
      tabsManager.triggerTabActivate(activeKey);
    }
    
    previousActiveKey.current = activeKey;
  }, [activeKey]);

  // 标签页激活时保存状态
  useEffect(() => {
    if (activeKey) {
      console.log('🔄 标签页激活:', activeKey);
      
              // 保存当前标签页的URL状态到sessionStorage
      const currentUrl = new URL(window.location.href);
      const currentTabId = currentUrl.searchParams.get('tabId');
      
      if (currentTabId && currentTabId === activeKey) {
        // 提取URL状态参数
        const urlState: any = {
          pagination: {},
          filters: {},
          sorting: {}
        };
        
        // 解析分页参数
        const page = currentUrl.searchParams.get(`${currentTabId}_page`);
        const pageSize = currentUrl.searchParams.get(`${currentTabId}_pageSize`);
        if (page) urlState.pagination.current = parseInt(page);
        if (pageSize) urlState.pagination.pageSize = parseInt(pageSize);
        
        // 解析筛选参数
        currentUrl.searchParams.forEach((value, key) => {
          if (key.startsWith(`${currentTabId}_filter_`)) {
            const filterKey = key.replace(`${currentTabId}_filter_`, '');
            if (value && value !== '') {
              urlState.filters[filterKey] = value;
            }
          }
        });
        
        // 解析排序参数
        const sortField = currentUrl.searchParams.get(`${currentTabId}_sortField`);
        const sortOrder = currentUrl.searchParams.get(`${currentTabId}_sortOrder`);
        if (sortField) urlState.sorting.field = sortField;
        if (sortOrder) urlState.sorting.order = sortOrder;
        
        // 保存到sessionStorage
        if (Object.keys(urlState.filters).length > 0 || 
            Object.keys(urlState.pagination).length > 0 || 
            Object.keys(urlState.sorting).length > 0) {
          storage.setItem(`tab_url_state_${currentTabId}`, urlState);
          console.log('💾 自动保存标签页URL状态:', currentTabId, urlState);
        }
      }
      
      // 恢复标签页状态
      restoreTabState(activeKey);
      
      // 手动检查并恢复状态（备用方案）
      setTimeout(() => {
        const url = new URL(window.location.href);
        const tabId = url.searchParams.get('tabId');
        
        if (tabId === activeKey) {
          // 检查URL是否包含状态参数
          const hasStateParams = Array.from(url.searchParams.entries()).some(([key, value]) => 
            key !== 'tabId' && (key.includes('_page') || key.includes('_filter_') || key.includes('_sort'))
          );
          
          if (!hasStateParams) {
            console.log('🔄 手动检查：URL缺少状态参数，尝试从sessionStorage恢复');
            const savedState = storage.getItem(`tab_url_state_${activeKey}`);
            if (savedState) {
              const urlState = savedState;
              console.log('🔄 手动恢复：从sessionStorage获取的状态:', urlState);
                
              // 构建包含状态的URL
              const urlWithState = buildUrlWithState(url.pathname, activeKey, urlState);
              console.log('🔗 手动恢复：构建的URL:', urlWithState);
                
              // 如果URL不同，则导航
              if (urlWithState !== window.location.href) {
                console.log('🔄 手动恢复：导航到包含状态的URL');
                navigate(urlWithState, { replace: true });
              }
            }
          } else {
            console.log('✅ 手动检查：URL已包含状态参数');
          }
        }
      }, 200); // 200ms延迟，确保其他逻辑完成
    }
  }, [activeKey, restoreTabState, buildUrlWithState, navigate]);

  // 监听用户切换，自动清理旧用户数据（使用稳定用户标识，避免 token 刷新误判为换用户导致标签条被清空）
  useEffect(() => {
    const checkUserChange = () => {
      const currentToken = localStorage.getItem('token');
      const currentStableId = getStableUserIdFromToken(currentToken);
      if (currentStableId && currentUserId.current !== currentStableId) {
        console.log('🔄 检测到用户切换，清理旧用户的标签页数据');
        // 清理标签页相关的SessionStorage数据
        sessionStorage.removeItem('dashboard_tabs');
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('tab_state_') || key.startsWith('tab_url_state_')) {
            sessionStorage.removeItem(key);
            console.log('🧹 清理旧用户标签页状态:', key);
          }
        });
        currentUserId.current = currentStableId;
        
        // 重置标签页状态
        setTabs([]);
        setActiveKey('');
        initialized.current = false;
        
        console.log('✅ 旧用户数据清理完成，标签页状态已重置');
      }
    };
    
    // 定期检查用户切换
    const userCheckInterval = setInterval(checkUserChange, 5000); // 每5秒检查一次
    
    return () => clearInterval(userCheckInterval);
  }, []);

  // 监听路由变化，自动保存当前标签页状态
  useEffect(() => {
    const handleRouteChange = () => {
      if (activeKey) {
        console.log('🔄 路由变化，自动保存当前标签页状态:', activeKey);
        
        const currentUrl = new URL(window.location.href);
        const currentTabId = currentUrl.searchParams.get('tabId');
        
        if (currentTabId && currentTabId === activeKey) {
          // 延迟保存，确保URL参数已经更新
          setTimeout(() => {
            const updatedUrl = new URL(window.location.href);
            const urlState: any = {
              pagination: {},
              filters: {},
              sorting: {}
            };
            
            // 解析分页参数
            const page = updatedUrl.searchParams.get(`${currentTabId}_page`);
            const pageSize = updatedUrl.searchParams.get(`${currentTabId}_pageSize`);
            if (page) urlState.pagination.current = parseInt(page);
            if (pageSize) urlState.pagination.pageSize = parseInt(pageSize);
            
            // 解析筛选参数
            updatedUrl.searchParams.forEach((value, key) => {
              if (key.startsWith(`${currentTabId}_filter_`)) {
                const filterKey = key.replace(`${currentTabId}_filter_`, '');
                if (value && value !== '') {
                  urlState.filters[filterKey] = value;
                }
              }
            });
            
            // 解析排序参数
            const sortField = updatedUrl.searchParams.get(`${currentTabId}_sortField`);
            const sortOrder = updatedUrl.searchParams.get(`${currentTabId}_sortOrder`);
            if (sortField) urlState.sorting.field = sortField;
            if (sortOrder) urlState.sorting.order = sortOrder;
            
            // 保存到sessionStorage
            if (Object.keys(urlState.filters).length > 0 || 
                Object.keys(urlState.pagination).length > 0 || 
                Object.keys(urlState.sorting).length > 0) {
              storage.setItem(`tab_url_state_${currentTabId}`, urlState);
              console.log('💾 路由变化后保存标签页URL状态:', currentTabId, urlState);
            }
          }, 100); // 100ms延迟，确保URL已更新
        }
      }
    };
    
    // 监听popstate事件（浏览器前进/后退）
    window.addEventListener('popstate', handleRouteChange);
    
    // 监听hashchange事件（URL hash变化）
    window.addEventListener('hashchange', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('hashchange', handleRouteChange);
    };
  }, [activeKey]);

  // 清理URL中的tabId参数
  const cleanupTabIdFromUrl = useCallback((tabKey: string) => {
    const currentUrl = new URL(window.location.href);
    const searchParams = currentUrl.searchParams;
    
    // 移除tabId参数
    searchParams.delete('tabId');
    
    // 移除所有以tabKey开头的参数（分页、筛选、排序等）
    Array.from(searchParams.keys()).forEach(key => {
      if (key.startsWith(tabKey + '_')) {
        searchParams.delete(key);
      }
    });
    
    // 更新URL（不触发页面刷新）
    const newUrl = searchParams.toString() ? 
      `${currentUrl.pathname}?${searchParams.toString()}` : 
      currentUrl.pathname;
    
    window.history.replaceState({}, '', newUrl);
    console.log(`🧹 清理URL中的tabId参数: ${tabKey}`);
  }, []);

  // 切换标签页
  const handleTabClick = useCallback((tab: EnhancedTabItem) => {
    console.log('🔄 标签页切换开始:', { 
      from: activeKey, 
      to: tab.key, 
      path: tab.path,
      currentUrl: window.location.href 
    });
    
    // 保存当前标签页状态
    if (activeKey && activeKey !== tab.key) {
      console.log('💾 保存当前标签页状态:', activeKey);
      tabsManager.triggerTabDeactivate(activeKey);
    }
    
    setActiveKey(tab.key);
    tabsManager.setActiveTab(tab.key);
    
    // 构建完整的URL（包含tabId和状态参数）
    const urlWithParams = buildTabUrl(tab.path, tab.key);
    console.log('🔗 标签页切换 - 目标URL:', urlWithParams);
    
    // 使用replace模式导航，避免在历史记录中创建重复条目
    startTransition(() => {
      navigate(urlWithParams, { replace: true });
    });
    
    onTabChange?.(tab.key);
    
    console.log('✅ 标签页切换完成:', tab.key);
  }, [activeKey, navigate, onTabChange, buildTabUrl]);


  // 关闭标签页（修复：确保正确关闭和状态处理）
  const handleCloseTab = useCallback((e: React.MouseEvent, tab: EnhancedTabItem) => {
    e.stopPropagation();
    if (!tab.closable) return;

    console.log('🚪 关闭标签页:', tab.key, '路径:', tab.path);

    // 触发标签页关闭事件
    tabsManager.triggerTabClose(tab.key);
    
    // 检查是否需要保留状态
    const config = tab.config?.stateManagement;
    if (config && !config.clearOnClose) {
      // 保留状态（如入库记录列表页）
      console.log('💾 保留标签页状态:', tab.key);
    } else {
      // 清空状态（默认行为）
      tabsManager.clearTabState(tab.key);
      console.log('🧹 清理标签页状态:', tab.key);
    }

    // 从标签页列表中移除
    setTabs(prev => {
      const newTabs = prev.filter(t => t.key !== tab.key);
      console.log('📋 标签页列表更新:', prev.length, '->', newTabs.length);
      
      // 如果关闭的是当前标签页，则切换到最后一个标签页
      if (tab.key === activeKey && newTabs.length > 0) {
        const lastTab = newTabs[newTabs.length - 1];
        console.log('🔄 切换到最后一个标签页:', lastTab.key);
        handleTabClick(lastTab);
      }
      return newTabs;
    });

    // 如果关闭的是当前标签页，清理URL中的tabId参数
    if (tab.key === activeKey) {
      cleanupTabIdFromUrl(tab.key);
    }
  }, [activeKey, handleTabClick]);

  // 🔑 新增：关闭指定标签页的API
  const closeTab = useCallback((tabKey: string): boolean => {
    console.log('🔒 TabsManager.closeTab API 被调用:', tabKey);
    
    // 查找要关闭的标签页
    const tabToClose = tabs.find(tab => tab.key === tabKey);
    if (!tabToClose) {
      console.warn('⚠️ 未找到要关闭的标签页:', tabKey);
      return false;
    }
    
    if (!tabToClose.closable) {
      console.warn('⚠️ 标签页不可关闭:', tabKey);
      return false;
    }
    
    // 调用现有的关闭逻辑，传入模拟的点击事件
    const mockEvent = new MouseEvent('click') as any;
    handleCloseTab(mockEvent, tabToClose);
    
    console.log('✅ 通过 API 成功关闭标签页:', tabKey);
    return true;
  }, [tabs, handleCloseTab]);
  
  // 🔑 新增：关闭当前标签页的API
  const closeCurrentTab = useCallback((): boolean => {
    if (activeKey) {
      console.log('🔒 TabsManager.closeCurrentTab API 被调用:', activeKey);
      return closeTab(activeKey);
    } else {
      console.warn('⚠️ 没有活动标签页可关闭');
      return false;
    }
  }, [activeKey, closeTab]);
  
  // 🔑 新增：通过路径关闭标签页的API
  const closeTabByPath = useCallback((path: string): boolean => {
    console.log('🔒 TabsManager.closeTabByPath API 被调用:', path);
    
    const tabToClose = tabs.find(tab => tab.path === path);
    if (tabToClose) {
      return closeTab(tabToClose.key);
    } else {
      console.warn('⚠️ 未找到路径对应的标签页:', path);
      return false;
    }
  }, [tabs, closeTab]);
  
  // 批量关闭功能（基于现有 closeTab，不改动状态/存储通道）
  const closeMany = useCallback((keys: string[]) => {
    // 去重并顺序关闭
    const uniqueKeys = Array.from(new Set(keys));
    uniqueKeys.forEach(k => closeTab(k));
  }, [closeTab]);

  const closeRightOf = useCallback((baseKey: string) => {
    const idx = tabs.findIndex(t => t.key === baseKey);
    if (idx === -1) return;
    const keys = tabs.slice(idx + 1).filter(t => t.closable !== false).map(t => t.key);
    closeMany(keys);
  }, [tabs, closeMany]);

  const closeLeftOf = useCallback((baseKey: string) => {
    const idx = tabs.findIndex(t => t.key === baseKey);
    if (idx <= 0) return;
    const keys = tabs.slice(0, idx).filter(t => t.closable !== false).map(t => t.key);
    closeMany(keys);
  }, [tabs, closeMany]);

  const closeOthersOf = useCallback((baseKey: string) => {
    const keys = tabs.filter(t => t.key !== baseKey && t.closable !== false).map(t => t.key);
    closeMany(keys);
  }, [tabs, closeMany]);

  const closeAllTabs = useCallback(() => {
    const keys = tabs.filter(t => t.closable !== false).map(t => t.key);
    closeMany(keys);
  }, [tabs, closeMany]);

  // 🔑 新增：获取标签页信息的API
  const getTab = useCallback((tabKey: string): EnhancedTabItem | null => {
    return tabs.find(tab => tab.key === tabKey) || null;
  }, [tabs]);
  
  // 🔑 新增：检查标签页是否存在的API
  const hasTab = useCallback((tabKey: string): boolean => {
    return tabs.some(tab => tab.key === tabKey);
  }, [tabs]);
  
  // 🔑 新增：获取所有标签页的API
  const getAllTabs = useCallback((): EnhancedTabItem[] => {
    return [...tabs];
  }, [tabs]);
  
  // 🔑 关键：将API方法暴露到全局，供其他页面调用
  useEffect(() => {
    // 暴露到 window 对象，供其他页面调用
    (window as any).tabsManager = {
      // 打开相关API：在“系统内标签页”中打开指定路径
      // - openTabByPath(path): 若存在同 path 标签页则切换；否则新建
      // - openTabByPath(path, { forceNew: true }): 强制新建一个标签页
      openTabByPath: (path: string, opts?: { forceNew?: boolean }) => {
        try {
          const p = String(path || '').trim();
          if (!p) return;
          const forceNew = !!(opts as any)?.forceNew;
          if (forceNew) {
            createNewTab(p);
          } else {
            smartCreateOrSwitchTab(p);
          }
        } catch (e) {
          try { console.warn('TabsManager.openTabByPath failed', e); } catch {}
        }
      },

      // 关闭相关API
      closeTab,
      closeCurrentTab,
      closeTabByPath,
      
      // 查询相关API
      getTab,
      hasTab,
      getAllTabs,
      getActiveTab: () => activeKey,
      
      // 状态管理API（复用现有的tabsManager实例方法）
      saveTabState: (tabId: string, state: any) => tabsManager.saveTabState(tabId, state),
      restoreTabState: (tabId: string) => tabsManager.restoreTabState(tabId),
      clearTabState: (tabId: string) => tabsManager.clearTabState(tabId),
      getTabState: (tabId: string) => tabsManager.getTabState(tabId),
      hasTabState: (tabId: string) => tabsManager.hasTabState(tabId),
      updateTabState: (tabId: string, updates: any) => tabsManager.updateTabState(tabId, updates),
      
      // 版本信息
      version: '1.0.0',
      apiName: 'TabsManager API'
    };
    
    console.log('🌍 TabsManager API 已暴露到全局:', {
      openTabByPath: typeof (window as any).tabsManager?.openTabByPath,
      closeTab: typeof closeTab,
      closeCurrentTab: typeof closeCurrentTab,
      closeTabByPath: typeof closeTabByPath,
      getTab: typeof getTab,
      hasTab: typeof hasTab,
      getAllTabs: typeof getAllTabs
    });
    
    return () => {
      // 清理时移除全局引用
      delete (window as any).tabsManager;
      console.log('🧹 TabsManager API 已从全局移除');
    };
  }, [closeTab, closeCurrentTab, closeTabByPath, getTab, hasTab, getAllTabs, activeKey]);

  const visualActiveKey = (() => {
    const list = Array.isArray(tabs) ? tabs : [];
    if (!list.length) return activeKey;
    if (activeKey && list.some(t => t.key === activeKey)) return activeKey;
    try {
      const sp = new URLSearchParams(location.search || '');
      const tid = String(sp.get('tabId') || '').trim();
      if (tid && list.some(t => t.key === tid)) return tid;
    } catch {}
    const exact = list.filter(t => String(t?.path || '') === String(location.pathname || ''));
    if (exact.length > 0) return String(exact[exact.length - 1]?.key || activeKey || '');
    const norm = (p: string) => String(p || '').replace(/\/\d+/g, '/:id').replace(/\/+$/, '');
    const locNorm = norm(String(location.pathname || ''));
    const normalized = list.filter(t => norm(String(t?.path || '')) === locNorm);
    if (normalized.length > 0) return String(normalized[normalized.length - 1]?.key || activeKey || '');
    return activeKey;
  })();

  return (
    <div className="bg-white border-b">
      <div className="flex items-center space-x-1 px-4 py-2 overflow-x-auto">
        {tabs.map(tab => (
          <div
            key={tab.key}
            className={`
              relative flex items-center px-3 py-1.5 rounded-md cursor-pointer select-none
              transition-all duration-150
              ${visualActiveKey === tab.key 
                ? 'bg-blue-100 text-blue-700 shadow-sm ring-1 ring-blue-300 font-semibold'
                : 'text-gray-600 hover:bg-gray-100'
              }
            `}
            onClick={() => handleTabClick(tab)}
            onContextMenu={(e) => {
              e.preventDefault();
              setCtxBaseKey(tab.key);
              setCtxPos({ x: e.clientX, y: e.clientY });
              setCtxVisible(true);
              setShowBatchMenu(false);
            }}
          >
            {tab.icon && <span className="mr-1">{tab.icon}</span>}
            <span className="text-sm">{getTabTitle(tab.path)}</span>
            {tab.closable && (
              <XMarkIcon 
                className={`w-4 h-4 ml-1 hover:text-red-500 ${visualActiveKey === tab.key ? 'text-blue-700' : ''}`}
                onClick={(e) => handleCloseTab(e, tab)}
              />
            )}
          </div>
        ))}
        {/* 批量关闭菜单触发按钮 */}
        <div className="ml-auto relative">
          <button
            className="flex items-center px-2 py-1 text-gray-600 hover:text-blue-600"
            onClick={(e) => {
              setBatchMenuPos({ x: (e as any).clientX, y: (e as any).clientY });
              setShowBatchMenu(v => !v);
              setCtxVisible(false);
            }}
            title={t('tabs.batchClose')}
          >
            <EllipsisVerticalIcon className="w-5 h-5" />
          </button>
          {showBatchMenu && (
            <div
              className="w-44 bg-white border rounded shadow text-sm"
              style={{ position: 'fixed', top: batchMenuPos.y + 6, left: batchMenuPos.x - 176, zIndex: 9999 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-3 py-2 hover:bg-gray-50 cursor-pointer" onClick={() => { setShowBatchMenu(false); closeRightOf(activeKey); }}>{t('tabs.closeRight')}</div>
              <div className="px-3 py-2 hover:bg-gray-50 cursor-pointer" onClick={() => { setShowBatchMenu(false); closeLeftOf(activeKey); }}>{t('tabs.closeLeft')}</div>
              <div className="px-3 py-2 hover:bg-gray-50 cursor-pointer" onClick={() => { setShowBatchMenu(false); closeOthersOf(activeKey); }}>{t('tabs.closeOthers')}</div>
              <div className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-red-600" onClick={() => { setShowBatchMenu(false); closeAllTabs(); }}>{t('tabs.closeAll')}</div>
            </div>
          )}
        </div>
      </div>

      {/* 右键菜单（固定定位，避免被父层 overflow 裁剪） */}
      {ctxVisible && (
        <div
          className="w-44 bg-white border rounded shadow text-sm"
          style={{ position: 'fixed', top: ctxPos.y, left: ctxPos.x, zIndex: 9999 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 hover:bg-gray-50 cursor-pointer" onClick={() => { setCtxVisible(false); closeRightOf(ctxBaseKey); }}>{t('tabs.closeRight')}</div>
          <div className="px-3 py-2 hover:bg-gray-50 cursor-pointer" onClick={() => { setCtxVisible(false); closeLeftOf(ctxBaseKey); }}>{t('tabs.closeLeft')}</div>
          <div className="px-3 py-2 hover:bg-gray-50 cursor-pointer" onClick={() => { setCtxVisible(false); closeOthersOf(ctxBaseKey); }}>{t('tabs.closeOthers')}</div>
          <div className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-red-600" onClick={() => { setCtxVisible(false); closeAllTabs(); }}>{t('tabs.closeAll')}</div>
        </div>
      )}
    </div>
  );
};

export default React.memo(TabsManager);
